import type { ConversationMessage, ConversationState, PublicWidgetConfig } from '@cbm/types';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchPublicConfig,
  initChat,
  pollConversationUpdates,
  sendMessage,
  submitLead,
} from '../lib/api';
import { resolveBootstrapConfig } from '../lib/runtime';
import { loadStoredSession, persistStoredSession } from '../lib/storage';
import { toIsoString } from '../lib/time';
import { Launcher } from './launcher';
import { LeadCaptureForm } from './lead-capture-form';
import { MessageList } from './message-list';
import { StatusBanner } from './status-banner';
import { SuggestedPrompts } from './suggested-prompts';

type WidgetRuntimeProps = {
  widgetKey: string;
  apiBaseUrl: string;
};

function makeLocalMessage(content: string): ConversationMessage {
  return {
    id: `local-${crypto.randomUUID()}`,
    conversationId: 'pending',
    projectId: 'pending',
    role: 'user',
    senderType: 'visitor',
    content,
    createdAt: new Date().toISOString(),
    deliveryState: 'queued',
  };
}

function parseMessageTime(value: ConversationMessage['createdAt']): number {
  const parsed = new Date(toIsoString(value)).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function isSemanticallyDuplicateMessage(
  existingMessage: ConversationMessage,
  incomingMessage: ConversationMessage,
): boolean {
  if (
    existingMessage.senderType !== incomingMessage.senderType ||
    existingMessage.role !== incomingMessage.role
  ) {
    return false;
  }

  if (existingMessage.content.trim() !== incomingMessage.content.trim()) {
    return false;
  }

  const timeDelta = Math.abs(
    parseMessageTime(existingMessage.createdAt) - parseMessageTime(incomingMessage.createdAt),
  );

  return timeDelta <= 30_000;
}

export function ChatWidget({ widgetKey, apiBaseUrl }: WidgetRuntimeProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initializingChat, setInitializingChat] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<PublicWidgetConfig | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [session, setSession] = useState(() => loadStoredSession(widgetKey));
  const [state, setState] = useState<ConversationState>('ai_active');
  const initializingChatRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      try {
        const publicConfig = await fetchPublicConfig(apiBaseUrl, widgetKey);

        if (!mounted) {
          return;
        }

        setConfig(publicConfig);
      } catch (bootstrapError) {
        if (mounted) {
          setError(
            bootstrapError instanceof Error
              ? bootstrapError.message
              : 'Δεν ήταν δυνατή η φόρτωση του chat.',
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void bootstrap();
    return () => {
      mounted = false;
    };
  }, [apiBaseUrl, widgetKey]);

  useEffect(() => {
    if (!open || chatInitialized || initializingChatRef.current) {
      return;
    }

    let mounted = true;

    async function initializeChatSession() {
      initializingChatRef.current = true;
      setInitializingChat(true);
      setError(null);

      try {
        const chatSession = await initChat(apiBaseUrl, {
          widgetKey,
          sessionId: session.sessionId,
          visitorId: session.visitorId,
        });

        if (!mounted) {
          return;
        }

        const nextSession = {
          projectId: chatSession.projectId,
          sessionId: chatSession.sessionId,
          visitorId: chatSession.visitorId,
          conversationId: chatSession.conversationId,
          conversationToken: chatSession.conversationToken,
          lastMessageAt: chatSession.messages.at(-1)
            ? toIsoString(chatSession.messages.at(-1)?.createdAt)
            : undefined,
        };

        setMessages(chatSession.messages);
        setState(chatSession.state);
        setSession(nextSession);
        setChatInitialized(true);
        persistStoredSession(widgetKey, nextSession);
      } catch (chatError) {
        if (mounted) {
          setError(
            chatError instanceof Error
              ? chatError.message
              : 'Δεν ήταν δυνατή η φόρτωση του chat.',
          );
        }
      } finally {
        initializingChatRef.current = false;
        if (mounted) {
          setInitializingChat(false);
        }
      }
    }

    void initializeChatSession();

    return () => {
      mounted = false;
    };
  }, [
    apiBaseUrl,
    chatInitialized,
    open,
    session.sessionId,
    session.visitorId,
    widgetKey,
  ]);

  useEffect(() => {
    if (!open || !session.conversationId || !session.conversationToken || !session.projectId) {
      return;
    }

    const pollId = window.setInterval(async () => {
      try {
        const response = await pollConversationUpdates(apiBaseUrl, {
          widgetKey,
          projectId: session.projectId!,
          conversationId: session.conversationId!,
          conversationToken: session.conversationToken!,
          after: session.lastMessageAt,
        });

        if (response.messages.length) {
          setMessages((current) => {
            const existingIds = new Set(current.map((message) => message.id));
            const merged = [...current];
            response.messages.forEach((message) => {
              const duplicate = merged.some(
                (existingMessage) =>
                  existingMessage.id === message.id ||
                  isSemanticallyDuplicateMessage(existingMessage, message),
              );

              if (!existingIds.has(message.id) && !duplicate) {
                merged.push(message);
              }
            });
            return merged;
          });

          const lastMessageAt = toIsoString(response.messages.at(-1)?.createdAt);
          const next = { ...session, lastMessageAt };
          setSession(next);
          persistStoredSession(widgetKey, next);
        }

        setState(response.state);
      } catch {
        // Polling is best effort so the widget stays usable even if refreshes fail.
      }
    }, 4000);

    return () => {
      window.clearInterval(pollId);
    };
  }, [apiBaseUrl, open, session, widgetKey]);

  const promptSuggestions = useMemo(
    () => (messages.length > 1 ? [] : config?.suggestedPrompts ?? []),
    [config?.suggestedPrompts, messages.length],
  );

  async function handleSend(content: string) {
    if (!content.trim() || !session.conversationId || !session.conversationToken || !session.projectId) {
      return;
    }

    setSending(true);
    setError(null);

    const optimistic = makeLocalMessage(content);
    setMessages((current) => [...current, optimistic]);
    setDraft('');

    try {
      const response = await sendMessage(apiBaseUrl, {
        widgetKey,
        projectId: session.projectId,
        conversationId: session.conversationId,
        conversationToken: session.conversationToken,
        content,
      });

      setMessages((current) => {
        const withoutOptimistic = current.filter((message) => message.id !== optimistic.id);
        return [
          ...withoutOptimistic,
          {
            ...optimistic,
            conversationId: session.conversationId!,
            projectId: session.projectId!,
            createdAt: new Date().toISOString(),
            deliveryState: 'sent',
          },
          response.message ?? {
            id: `reply-${crypto.randomUUID()}`,
            conversationId: session.conversationId!,
            projectId: session.projectId!,
            role: 'assistant',
            senderType:
              response.state === 'human_active' || response.state === 'waiting_for_human'
                ? 'system'
                : 'ai',
            content: response.reply,
            createdAt: new Date().toISOString(),
          },
        ];
      });

      setState(response.state);
      const lastMessageAt = new Date().toISOString();
      const next = { ...session, lastMessageAt };
      setSession(next);
      persistStoredSession(widgetKey, next);
    } catch (sendError) {
      setMessages((current) => current.filter((message) => message.id !== optimistic.id));
      setDraft(content);
      setError(
        sendError instanceof Error ? sendError.message : 'Δεν ήταν δυνατή η αποστολή του μηνύματος.',
      );
    } finally {
      setSending(false);
    }
  }

  async function handleLeadSubmit(payload: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    note?: string;
  }) {
    if (!session.conversationId || !session.conversationToken || !session.projectId) {
      return;
    }

    await submitLead(apiBaseUrl, {
      widgetKey,
      projectId: session.projectId,
      conversationId: session.conversationId,
      conversationToken: session.conversationToken,
      ...payload,
    });

    const confirmation: ConversationMessage = {
      id: `lead-${crypto.randomUUID()}`,
      conversationId: session.conversationId,
      projectId: session.projectId,
      role: 'system',
      senderType: 'system',
      content: 'Ευχαριστούμε. Τα στοιχεία σας στάλθηκαν στην ομάδα.',
      createdAt: new Date().toISOString(),
    };

    setMessages((current) => [...current, confirmation]);
  }

  if (loading) {
    return (
      <>
        <Launcher open={open} onClick={() => setOpen((current) => !current)} themeColor="#0C6A57" />
        {open ? <div className="cbm-panel">Φόρτωση…</div> : null}
      </>
    );
  }

  if (!config) {
    return null;
  }

  const handoffActive = state === 'human_active' || state === 'waiting_for_human';

  return (
    <>
      <Launcher
        open={open}
        onClick={() => setOpen((current) => !current)}
        themeColor={config.themeColor}
      />
      {open ? (
        <div
          className={`cbm-panel ${config.widgetPlacement === 'bottom-left' ? 'cbm-panel--left' : ''}`}
          style={
            {
              '--cbm-theme': config.themeColor,
              '--cbm-accent': config.accentColor,
              '--cbm-text': config.textColor,
            } as CSSProperties
          }
        >
          <div className="cbm-header">
            <div>
              <p className="cbm-eyebrow">{config.displayName}</p>
              <h3>{config.welcomeMessage}</h3>
            </div>
            <button type="button" className="cbm-icon-button" onClick={() => setOpen(false)}>
              ×
            </button>
          </div>

          <StatusBanner state={state} />
          {error ? <div className="cbm-error">{error}</div> : null}

          <MessageList messages={messages} loading={sending || initializingChat} />

          {!showLeadForm && !messages.length ? (
            <p className="cbm-helper">{config.helperText}</p>
          ) : null}

          {!showLeadForm ? (
            <SuggestedPrompts
              prompts={promptSuggestions}
              disabled={sending || initializingChat || !session.conversationId}
              onSelect={handleSend}
            />
          ) : null}

          {config.leadCaptureEnabled && showLeadForm ? (
            <LeadCaptureForm
              onSubmit={handleLeadSubmit}
              onClose={() => setShowLeadForm(false)}
            />
          ) : null}

          {!showLeadForm ? (
            <form
              className="cbm-composer"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSend(draft);
              }}
            >
              <textarea
                placeholder={
                  handoffActive
                    ? 'Το μήνυμά σας θα μείνει σε αναμονή για απάντηση από άνθρωπο.'
                    : 'Γράψτε το μήνυμά σας'
                }
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    if (!sending && state !== 'closed') {
                      void handleSend(draft);
                    }
                  }
                }}
                disabled={sending || initializingChat || state === 'closed'}
                rows={2}
              />
              <div className="cbm-composer__actions">
                {config.leadCaptureEnabled ? (
                  <button
                    type="button"
                    className="cbm-secondary-button"
                    disabled={initializingChat || !session.conversationId}
                    onClick={() => setShowLeadForm((current) => !current)}
                  >
                    Αφήστε στοιχεία
                  </button>
                ) : null}
                <button
                  type="submit"
                  className="cbm-primary-button"
                  disabled={!draft.trim() || sending || initializingChat || state === 'closed'}
                >
                  {sending ? 'Αποστολή…' : 'Αποστολή'}
                </button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export function mountWidgetFromCurrentScript() {
  return resolveBootstrapConfig();
}
